import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {
    IOT_METRICS,
    METRIC_COLORS,
    formatChartTick,
    formatKstDateTime,
    formatMetricValue,
    metricMeta
} from './iot_metrics';
import { CONNECTION_TONES, iotColor, iotFont, iotLayout, iotMixin, iotRadius } from './iot_console_theme';
import { buildReportModel, downloadIotReport } from './iot_report_export';

// 24시간만 원본, 나머지는 시간별 집계로 조회한다 — raw 구간 상한(7일)을 넘지 않는다.
export const RANGE_PRESETS = Object.freeze([
    { key: '24h', label: '최근 24시간', hours: 24, resolution: 'raw' },
    { key: '7d', label: '최근 7일', hours: 24 * 7, resolution: 'hourly' },
    { key: '15d', label: '최근 15일', hours: 24 * 15, resolution: 'hourly' },
    { key: '30d', label: '최근 30일', hours: 24 * 30, resolution: 'hourly' },
    { key: '3m', label: '최근 3개월', months: 3, resolution: 'hourly' },
    { key: '6m', label: '최근 6개월', months: 6, resolution: 'hourly' }
]);

export function rangeStart(to, range) {
    if (!range.months) return new Date(to.getTime() - range.hours * 60 * 60 * 1000);
    // 브라우저 시간대와 무관하게 KST 달력에서 빼고, 월말은 대상 월의 마지막 날로 맞춘다.
    const kst = new Date(to.getTime() + 9 * 60 * 60 * 1000);
    const day = kst.getUTCDate();
    kst.setUTCDate(1);
    kst.setUTCMonth(kst.getUTCMonth() - range.months);
    const lastDay = new Date(Date.UTC(kst.getUTCFullYear(), kst.getUTCMonth() + 1, 0)).getUTCDate();
    kst.setUTCDate(Math.min(day, lastDay));
    return new Date(kst.getTime() - 9 * 60 * 60 * 1000);
}

// 상세 화면은 수집·목록의 8종 계약과 분리해 폼알데하이드를 표시하거나 조회하지 않는다.
export const DETAIL_METRIC_KEYS = Object.freeze([
    'pm1_0', 'pm2_5', 'pm10', 'voc', 'co2', 'temperature', 'humidity'
]);

export const DETAIL_METRICS = Object.freeze(
    DETAIL_METRIC_KEYS.map((metricKey) => IOT_METRICS.find((item) => item.key === metricKey))
);

// 단위와 스케일이 같은 지표만 한 축에 묶는다. 상세 센서는 정확히 한 그룹에 포함한다.
export const GRAPH_GROUPS = Object.freeze([
    Object.freeze({ key: 'particulate', metrics: Object.freeze(['pm1_0', 'pm2_5', 'pm10']), variant: 'line' }),
    Object.freeze({ key: 'co2', metrics: Object.freeze(['co2']), variant: 'area' }),
    Object.freeze({ key: 'voc', metrics: Object.freeze(['voc']), variant: 'area' }),
    Object.freeze({ key: 'temperature', metrics: Object.freeze(['temperature']), variant: 'area' }),
    Object.freeze({ key: 'humidity', metrics: Object.freeze(['humidity']), variant: 'area' })
]);

export function particulateLegendItemSorter(entry) {
    const metricKey = typeof entry === 'string' ? entry : entry?.dataKey || entry?.value;
    const rank = GRAPH_GROUPS[0].metrics.indexOf(metricKey);
    return rank === -1 ? GRAPH_GROUPS[0].metrics.length : rank;
}

export function mergeMetricSeries(seriesByMetric, metricKeys) {
    const rowsByTime = new Map();
    metricKeys.forEach((metricKey) => {
        (seriesByMetric?.[metricKey] || []).forEach((point) => {
            const row = rowsByTime.get(point.t) || { t: point.t };
            row[metricKey] = point.value;
            rowsByTime.set(point.t, row);
        });
    });
    return Array.from(rowsByTime.values()).sort((left, right) => Date.parse(left.t) - Date.parse(right.t));
}

function numericValues(points) {
    return (points || []).map((point) => Number(point.value)).filter((value) => Number.isFinite(value));
}

// 선택 기간 첫 값 대비 마지막 값의 변화율. 점이 2개 미만이거나 첫 값이 0이면 표시하지 않는다.
export function seriesDelta(points) {
    const values = numericValues(points);
    if (values.length < 2 || values[0] === 0) return null;
    const percent = ((values[values.length - 1] - values[0]) / Math.abs(values[0])) * 100;
    const rounded = Math.round(percent * 10) / 10;
    return { text: `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}%`, direction: Math.sign(rounded) };
}

const SPARK_WIDTH = 130;
const SPARK_HEIGHT = 28;
const SPARK_MAX_POINTS = 60;

// 카드 스파크라인 — 시계열을 최대 60점으로 솎아 130×28 상자 안의 선·면 경로로 만든다.
export function sparklinePaths(points) {
    const all = numericValues(points);
    if (all.length < 2) return null;
    const stride = Math.ceil(all.length / SPARK_MAX_POINTS);
    const values = all.filter((_, index) => index % stride === 0 || index === all.length - 1);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const line = values.map((value, index) => {
        const x = 1 + ((SPARK_WIDTH - 2) * index) / (values.length - 1);
        const ratio = max === min ? 0.5 : (value - min) / (max - min);
        const y = SPARK_HEIGHT - 3 - ratio * (SPARK_HEIGHT - 6);
        return `${index ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
    return { line, area: `${line} L${SPARK_WIDTH - 1} ${SPARK_HEIGHT} L1 ${SPARK_HEIGHT} Z` };
}

function tooltipValue(value, metricKey) {
    const meta = metricMeta(metricKey);
    return [`${formatMetricValue(metricKey, value)} ${meta?.unit || ''}`, meta?.label || metricKey];
}

const KST_TIME = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
});
const KST_DATE = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit'
});

function relativeTime(parsed, now = Date.now()) {
    const seconds = Math.max(0, Math.round((now - parsed) / 1000));
    if (seconds < 60) return `${seconds}초 전`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}분 전`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}시간 전`;
    return `${Math.floor(seconds / 86400)}일 전`;
}

function lastSeenFact(value) {
    const parsed = Date.parse(value || '');
    if (!Number.isFinite(parsed)) return { value: '-', sub: '수신 기록 없음' };
    return { value: KST_TIME.format(parsed), sub: `${KST_DATE.format(parsed)} · ${relativeTime(parsed)}` };
}

function ChartFrame({ loading, data, compact = false, children }) {
    return (
        <ChartViewport $compact={compact}>
            {loading ? (
                <ChartEmpty>측정값을 불러오는 중입니다.</ChartEmpty>
            ) : data.length === 0 ? (
                <ChartEmpty>선택한 기간에 수집된 측정값이 없습니다.</ChartEmpty>
            ) : children}
        </ChartViewport>
    );
}

const AXIS_TICK = { fontSize: 11, fill: iotColor.textMuted };

// 조회 데이터 최대값의 2배. 모두 0인 경우에만 퇴화축 방지용 상한 1을 쓴다.
export function chartYDomain([dataMin, dataMax]) {
    const min = Number.isFinite(dataMin) ? dataMin : 0;
    const max = Number.isFinite(dataMax) ? dataMax : 0;
    const lower = min < 0 ? min * 2 : 0;
    const upper = max > 0 ? max * 2 : (min < 0 ? 0 : 1);
    return [lower, upper];
}

function SingleMetricChart({ metricKey, title, caption, points, loading, range, current }) {
    const meta = metricMeta(metricKey);
    const data = mergeMetricSeries({ [metricKey]: points }, [metricKey]);
    const titleId = `iot-${metricKey}-chart-title`;
    return (
        <ChartPanel aria-labelledby={titleId} $compact>
            <ChartPanelHeader>
                <div>
                    <h3 id={titleId}>{title}</h3>
                    <p>{caption}</p>
                </div>
                <CurrentValue>
                    <strong>{current}</strong>
                    <span>{meta?.unit}</span>
                </CurrentValue>
            </ChartPanelHeader>
            <ChartFrame loading={loading} data={data} compact>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 10, right: 8, bottom: 2, left: -12 }}>
                        <defs>
                            <linearGradient id={`iot-fill-${metricKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={METRIC_COLORS[metricKey]} stopOpacity={0.24} />
                                <stop offset="95%" stopColor={METRIC_COLORS[metricKey]} stopOpacity={0.03} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid stroke={iotColor.rowLine} vertical={false} />
                        <XAxis dataKey="t" tickFormatter={formatChartTick} tick={{ ...AXIS_TICK, fontSize: 10.5 }} minTickGap={54} axisLine={{ stroke: iotColor.cardLine }} tickLine={false} />
                        <YAxis domain={chartYDomain} tick={{ ...AXIS_TICK, fontSize: 10.5 }} width={52} axisLine={false} tickLine={false} />
                        <Tooltip
                            labelFormatter={(value) => formatKstDateTime(value, { withSeconds: range.resolution === 'raw' })}
                            formatter={(value) => tooltipValue(value, metricKey)}
                        />
                        <Area
                            type="monotone"
                            dataKey={metricKey}
                            stroke={METRIC_COLORS[metricKey]}
                            strokeWidth={1.8}
                            fill={`url(#iot-fill-${metricKey})`}
                            dot={data.length === 1}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </ChartFrame>
        </ChartPanel>
    );
}

export function toKstParam(date) {
    const shifted = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    const pad = (value) => String(value).padStart(2, '0');
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`
        + `T${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}:${pad(shifted.getUTCSeconds())}+09:00`;
}

function readDeviceIdFromLocation(location) {
    if (location?.state?.deviceId) return String(location.state.deviceId);
    const fromQuery = new URLSearchParams(location?.search || '').get('device_id');
    return fromQuery ? String(fromQuery) : '';
}

function connectionLabel(device) {
    if (device?.connection_state === 'NEVER_CONNECTED') return '연결 대기';
    if (device?.connection_state === 'DISABLED' || device?.status === 'DISABLED') return '사용중지';
    return device?.connection_state === 'ONLINE' || device?.online ? '온라인' : '오프라인';
}

function productLabel(device) {
    return device?.provisioning_state === 'PROVISIONED' ? '등록 제품' : 'MQTT 자동발견';
}

function IotDeviceDetail() {
    const { permissions, role } = useOutletContext() || {};
    const canView = role === 'admin' || role === 'staff';
    const canManage = role === 'admin' || Boolean(permissions?.iot_manage);
    const location = useLocation();
    const navigate = useNavigate();
    const deviceId = readDeviceIdFromLocation(location);

    const [device, setDevice] = useState(null);
    const [rangeKey, setRangeKey] = useState(RANGE_PRESETS[0].key);
    const [seriesByMetric, setSeriesByMetric] = useState({});
    const [loading, setLoading] = useState(canView && Boolean(deviceId));
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [loadedRange, setLoadedRange] = useState(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState('xlsx');
    const [exportModel, setExportModel] = useState(null);
    const [exportBusy, setExportBusy] = useState(false);
    const [exportError, setExportError] = useState('');
    const [exportProgress, setExportProgress] = useState('');
    const exportButtonRef = useRef(null);
    const exportDialogRef = useRef(null);
    const readingsRequestRef = useRef(0);
    const exportBusyRef = useRef(false);

    const range = RANGE_PRESETS.find((preset) => preset.key === rangeKey) || RANGE_PRESETS[0];

    const loadDevice = useCallback(async () => {
        try {
            const response = await axios.get(`/api/admin/iot/devices/${encodeURIComponent(deviceId)}`);
            setDevice(response.data.device || null);
        } catch (loadError) {
            setError(loadError.response?.data?.message || 'IoT 장치 정보를 불러오지 못했습니다.');
        }
    }, [deviceId]);

    const loadReadings = useCallback(async () => {
        const request = ++readingsRequestRef.current;
        setLoading(true);
        setError('');
        setLoadedRange(null);
        try {
            const to = new Date();
            const from = rangeStart(to, range);
            const responses = await Promise.all(DETAIL_METRICS.map((item) => (
                axios.get(`/api/admin/iot/devices/${encodeURIComponent(deviceId)}/readings`, {
                    params: {
                        metric: item.key,
                        resolution: range.resolution,
                        from: toKstParam(from),
                        to: toKstParam(to)
                    }
                })
            )));
            if (request !== readingsRequestRef.current) return;
            setSeriesByMetric(Object.fromEntries(DETAIL_METRICS.map((item, index) => (
                [item.key, responses[index].data.points || []]
            ))));
            setLoadedRange({ from: toKstParam(from), to: toKstParam(to), resolution: range.resolution, rangeLabel: range.label, deviceId });
        } catch (loadError) {
            if (request !== readingsRequestRef.current) return;
            setError(loadError.response?.data?.message || '측정값을 불러오지 못했습니다.');
            setSeriesByMetric({});
        } finally {
            if (request === readingsRequestRef.current) setLoading(false);
        }
    }, [deviceId, range]);

    const closeExport = useCallback(() => {
        if (exportBusyRef.current) return;
        setExportOpen(false);
        exportButtonRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!exportOpen) return undefined;
        const before = document.activeElement;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        exportDialogRef.current?.querySelector('input:checked')?.focus();
        const keydown = (event) => {
            if (event.key === 'Escape') { event.preventDefault(); closeExport(); }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(exportDialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled)') || []);
            const first = focusable[0], last = focusable[focusable.length - 1];
            if (!first) { event.preventDefault(); exportDialogRef.current?.focus(); return; }
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', keydown);
        return () => { document.removeEventListener('keydown', keydown); document.body.style.overflow = overflow; before?.focus?.(); };
    }, [exportOpen, closeExport]);

    const openExport = () => {
        setExportError('');
        try {
            const model = buildReportModel({ ...loadedRange, deviceName: device?.display_name || device?.name || deviceId, seriesByMetric });
            setExportModel(model); setExportFormat('xlsx'); setExportOpen(true);
        } catch (reportError) { setNotice(reportError.message); }
    };

    const startExport = async () => {
        if (exportBusyRef.current || !exportModel) return;
        exportBusyRef.current = true; setExportBusy(true); setExportError('');
        try {
            const result = await downloadIotReport(exportFormat, exportModel, setExportProgress);
            setNotice(`${exportFormat === 'xlsx' ? '엑셀' : 'PDF'} 보고서 다운로드를 시작했습니다. (${result.filename})`);
            setExportOpen(false);
        } catch (reportError) { setExportError(reportError.message || '보고서를 만들지 못했습니다. 다시 시도해 주세요.'); }
        finally { exportBusyRef.current = false; setExportBusy(false); setExportProgress(''); }
    };

    useEffect(() => {
        if (canView && deviceId) loadDevice();
    }, [canView, deviceId, loadDevice]);

    useEffect(() => {
        if (canView && deviceId) loadReadings();
    }, [canView, deviceId, loadReadings]);

    const changeStatus = async (status) => {
        setError('');
        setNotice('');
        try {
            await axios.put(`/api/admin/iot/devices/${encodeURIComponent(deviceId)}`, { status });
            setNotice(status === 'DISABLED' ? '장치를 사용 중지했습니다.' : '장치를 사용 재개했습니다.');
            await loadDevice();
        } catch (statusError) {
            setError(statusError.response?.data?.message || '장치 상태를 변경하지 못했습니다.');
        }
    };

    if (!canView) {
        return <StatePanel role="alert">IoT 모니터링 권한이 필요합니다.</StatePanel>;
    }
    if (!deviceId) {
        return <StatePanel role="alert">장치를 선택하지 않았습니다. 목록에서 장치를 선택해 주세요.</StatePanel>;
    }

    const deviceName = device?.display_name || device?.name || deviceId;
    const state = connectionLabel(device);
    const owner = device?.owner;
    const ownerName = owner?.user_name || owner?.email || '소유자 없음';
    const ownerContact = owner
        ? [owner.email, owner.phone_num].filter((item) => item && item !== ownerName).join(' · ')
        : '';
    const shareText = device?.shared_member_count ? `${device.shared_member_count}명 공유` : '공유 없음';
    const lastSeen = lastSeenFact(device?.last_seen_at);
    const currentValue = (metricKey) => formatMetricValue(metricKey, device?.last_metrics?.[metricKey]);
    // 모듈 기종마다 보내는 센서가 달라(VOC·CO2 미탑재 기종) 고정 7종이 아니라 실제 최근값이 있는 수를 센다.
    const receivedMetricCount = DETAIL_METRIC_KEYS.filter((metricKey) => currentValue(metricKey) !== '-').length;
    const pointCount = Math.max(0, ...DETAIL_METRIC_KEYS.map((metricKey) => (seriesByMetric[metricKey] || []).length));
    const resolutionText = range.resolution === 'raw' ? '원본 1분 단위' : '시간별 평균';
    const particulateData = mergeMetricSeries(seriesByMetric, GRAPH_GROUPS[0].metrics);
    const particulateLegend = [...GRAPH_GROUPS[0].metrics].sort(
        (left, right) => particulateLegendItemSorter(left) - particulateLegendItemSorter(right)
    );

    return (
        <PageShell>
            <PageInner inert={exportOpen ? '' : undefined} aria-hidden={exportOpen ? true : undefined}>
                <Breadcrumb aria-label="이동 경로">
                    <button type="button" onClick={() => navigate('/iot_devices')}>IoT 모니터링</button>
                    <span aria-hidden="true">/</span>
                    <span>{deviceName}</span>
                </Breadcrumb>

                <HeroCard aria-label="장치 정보">
                    <div>
                        <TitleRow>
                            <PageTitle>{deviceName}</PageTitle>
                            <StatusChip $tone={CONNECTION_TONES[state]}><i />{state}</StatusChip>
                        </TitleRow>
                        <SubLine>
                            <span>{device?.location || '설치 위치 미등록'}</span>
                            {' · '}
                            <b>{deviceId}</b>
                            {' · '}
                            <span>{productLabel(device)}</span>
                        </SubLine>
                    </div>
                    <HeroDivider aria-hidden="true" />
                    <FactGrid>
                        <Fact title={formatKstDateTime(device?.last_measured_at, { withSeconds: true }) !== '-'
                            ? `최근 측정 ${formatKstDateTime(device?.last_measured_at, { withSeconds: true })}` : undefined}
                        >
                            <FactLabel>최근 수신</FactLabel>
                            <FactValue>{lastSeen.value}</FactValue>
                            <FactSub>{lastSeen.sub}</FactSub>
                        </Fact>
                        <Fact>
                            <FactLabel>소유자</FactLabel>
                            <FactValue>{ownerName}</FactValue>
                            <FactSub>
                                {ownerContact && <><span>{ownerContact}</span>{' · '}</>}
                                <span>{shareText}</span>
                            </FactSub>
                        </Fact>
                        <Fact>
                            <FactLabel>수집 지표</FactLabel>
                            <FactValue>{receivedMetricCount > 0 ? `${receivedMetricCount}종 센서` : '수신 전'}</FactValue>
                            <FactSub>{range.resolution === 'raw' ? '원본 1분 주기' : '시간별 평균 조회'}</FactSub>
                        </Fact>
                    </FactGrid>
                    <HeroActions>
                        <GhostButton type="button" onClick={() => navigate(`/iot_control_logs?device_id=${encodeURIComponent(deviceId)}`)}>IR 제어 로그</GhostButton>
                        {canManage && (device?.status === 'DISABLED' ? (
                            <PrimaryButton type="button" disabled={device?.has_telemetry_record === false} onClick={() => changeStatus('ACTIVE')}>사용 재개</PrimaryButton>
                        ) : (
                            <DangerButton type="button" disabled={device?.has_telemetry_record === false} onClick={() => changeStatus('DISABLED')}>사용 중지</DangerButton>
                        ))}
                    </HeroActions>
                </HeroCard>

                {error && <Message role="alert" $error>{error}</Message>}
                {notice && <Message role="status">{notice}</Message>}

                <MetricSummaryGrid aria-label="센서 최근값">
                    {DETAIL_METRICS.map((item) => {
                        const points = seriesByMetric[item.key];
                        const delta = loading ? null : seriesDelta(points);
                        const spark = loading ? null : sparklinePaths(points);
                        return (
                            <MetricValueCard key={item.key}>
                                <MetricCardTop>
                                    <MetricLabel>
                                        <MetricDot $color={METRIC_COLORS[item.key]} />
                                        {item.label}
                                    </MetricLabel>
                                    {delta && (
                                        <MetricDelta
                                            $direction={delta.direction}
                                            title={`${range.label} 첫 측정 대비`}
                                        >
                                            {delta.text}
                                        </MetricDelta>
                                    )}
                                </MetricCardTop>
                                <MetricReading>
                                    <strong>{currentValue(item.key)}</strong>
                                    <span>{item.unit}</span>
                                </MetricReading>
                                <Sparkline viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
                                    {spark && (
                                        <>
                                            <path d={spark.area} fill={`${METRIC_COLORS[item.key]}22`} stroke="none" />
                                            <path d={spark.line} fill="none" stroke={METRIC_COLORS[item.key]} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                                        </>
                                    )}
                                </Sparkline>
                            </MetricValueCard>
                        );
                    })}
                </MetricSummaryGrid>

                <Controls>
                    <h2>센서 전체 추이</h2>
                    <RangeTabs role="group" aria-label="기간 선택">
                        {RANGE_PRESETS.map((preset) => (
                            <RangeTab
                                key={preset.key}
                                type="button"
                                $active={preset.key === rangeKey}
                                aria-pressed={preset.key === rangeKey}
                                onClick={() => setRangeKey(preset.key)}
                            >
                                {preset.label}
                            </RangeTab>
                        ))}
                    </RangeTabs>
                    <ResolutionNote>
                        {loading ? resolutionText : `${resolutionText} · ${pointCount.toLocaleString('ko-KR')} 포인트`}
                    </ResolutionNote>
                    <ExportTrigger ref={exportButtonRef} type="button" disabled={loading || !loadedRange || !pointCount || Boolean(error)} onClick={openExport} aria-haspopup="dialog">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M7 8V3h10v5M7 17H4V9h16v8h-3M7 14h10v7H7zM16 11h1" /></svg>
                        다운로드
                    </ExportTrigger>
                </Controls>

                <ChartPanel aria-labelledby="iot-particulate-chart-title">
                    <ChartPanelHeader>
                        <div>
                            <h3 id="iot-particulate-chart-title">미세먼지 추이</h3>
                            <p>PM1.0 · PM2.5 · PM10을 같은 농도 축에서 비교합니다.</p>
                        </div>
                        <LegendRow>
                            {particulateLegend.map((metricKey) => (
                                <LegendItem key={metricKey}>
                                    <LegendSwatch $color={METRIC_COLORS[metricKey]} />
                                    {metricMeta(metricKey)?.label}
                                    <b>{currentValue(metricKey)}</b>
                                </LegendItem>
                            ))}
                            <UnitPill>㎍/㎥</UnitPill>
                        </LegendRow>
                    </ChartPanelHeader>
                    <ChartFrame loading={loading} data={particulateData}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={particulateData} margin={{ top: 12, right: 18, bottom: 4, left: 0 }}>
                                <CartesianGrid stroke={iotColor.rowLine} vertical={false} />
                                <XAxis dataKey="t" tickFormatter={formatChartTick} tick={AXIS_TICK} minTickGap={46} axisLine={{ stroke: iotColor.cardLine }} tickLine={false} />
                                <YAxis domain={chartYDomain} tick={AXIS_TICK} width={56} axisLine={false} tickLine={false} />
                                <Tooltip
                                    labelFormatter={(value) => formatKstDateTime(value, { withSeconds: range.resolution === 'raw' })}
                                    formatter={(value, name) => tooltipValue(value, name)}
                                />
                                {GRAPH_GROUPS[0].metrics.map((metricKey) => (
                                    <Line
                                        key={metricKey}
                                        type="monotone"
                                        dataKey={metricKey}
                                        stroke={METRIC_COLORS[metricKey]}
                                        strokeWidth={1.8}
                                        dot={particulateData.length === 1}
                                        isAnimationActive={false}
                                        connectNulls
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartFrame>
                </ChartPanel>

                <GasChartGrid>
                    <SingleMetricChart
                        metricKey="co2"
                        title="이산화탄소"
                        caption="큰 ppm 범위를 독립 축으로 표시합니다."
                        points={seriesByMetric.co2 || []}
                        loading={loading}
                        range={range}
                        current={currentValue('co2')}
                    />
                    <SingleMetricChart
                        metricKey="voc"
                        title="휘발성유기화합물"
                        caption={`작은 ${metricMeta('voc').unit} 변화를 면적으로 강조합니다.`}
                        points={seriesByMetric.voc || []}
                        loading={loading}
                        range={range}
                        current={currentValue('voc')}
                    />
                    <SingleMetricChart
                        metricKey="temperature"
                        title="온도 변화"
                        caption="℃ 변화를 독립 면적 그래프로 표시합니다."
                        points={seriesByMetric.temperature || []}
                        loading={loading}
                        range={range}
                        current={currentValue('temperature')}
                    />
                    <SingleMetricChart
                        metricKey="humidity"
                        title="습도 변화"
                        caption="% 변화를 독립 면적 그래프로 표시합니다."
                        points={seriesByMetric.humidity || []}
                        loading={loading}
                        range={range}
                        current={currentValue('humidity')}
                    />
                </GasChartGrid>
            </PageInner>
            {exportOpen && (
                <ExportOverlay onMouseDown={(event) => { if (event.target === event.currentTarget) closeExport(); }}>
                    <ExportDialog ref={exportDialogRef} role="dialog" aria-modal="true" aria-labelledby="iot-export-title" aria-describedby="iot-export-description" aria-busy={exportBusy} tabIndex={-1}>
                        <ExportHeading><h2 id="iot-export-title">보고서 다운로드</h2><button type="button" aria-label="다운로드 팝업 닫기" disabled={exportBusy} onClick={closeExport}>×</button></ExportHeading>
                        <p id="iot-export-description">다운로드할 파일 형식을 선택해 주세요.</p>
                        <ExportFacts><strong>{exportModel?.deviceName}</strong><span>{exportModel?.rangeLabel} · {exportModel?.aggregation}</span><small>{exportModel?.info}</small></ExportFacts>
                        <ExportOptions disabled={exportBusy}>
                            <legend>파일 형식</legend>
                            <ExportOption $selected={exportFormat === 'xlsx'}><input type="radio" name="iot-export-format" value="xlsx" checked={exportFormat === 'xlsx'} onChange={() => setExportFormat('xlsx')} /><span><strong>엑셀 (.xlsx)</strong><small>그래프 요약과 측정값 표 · 편집 가능한 차트</small></span></ExportOption>
                            <ExportOption $selected={exportFormat === 'pdf'}><input type="radio" name="iot-export-format" value="pdf" checked={exportFormat === 'pdf'} onChange={() => setExportFormat('pdf')} /><span><strong>PDF (.pdf)</strong><small>그래프와 전체 측정값 · A4 보고서</small></span></ExportOption>
                        </ExportOptions>
                        {exportError && <Message role="alert" $error>{exportError}</Message>}
                        {exportBusy && <p role="status" aria-live="polite">{exportProgress || '보고서를 만들고 있습니다.'}</p>}
                        <ExportActions><GhostButton type="button" disabled={exportBusy} onClick={closeExport}>취소</GhostButton><PrimaryButton type="button" disabled={exportBusy} onClick={startExport}>{exportBusy ? '생성 중…' : '다운로드'}</PrimaryButton></ExportActions>
                    </ExportDialog>
                </ExportOverlay>
            )}
        </PageShell>
    );
}

const ExportOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(9, 18, 31, 0.68);
`;
const ExportDialog = styled.div`
    width: min(100%, 490px);
    max-height: calc(100dvh - 40px);
    overflow-y: auto;
    box-sizing: border-box;
    padding: 26px;
    border: 1px solid ${iotColor.cardLine};
    border-radius: ${iotRadius.card};
    background: ${iotColor.surface};
    color: ${iotColor.text};
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.3);
    font-family: inherit;
    p { color: ${iotColor.textMuted}; font-size: 14px; line-height: 1.6; }
    @media (max-width: 480px) { padding: 20px; }
`;
const ExportHeading = styled.div`
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
    h2 { margin: 0; font-size: 21px; }
    button { border: 0; background: transparent; color: ${iotColor.textMuted}; cursor: pointer; font-size: 28px; padding: 2px 6px; }
`;
const ExportFacts = styled.div`
    display: flex; flex-direction: column; gap: 5px; padding: 14px 16px; margin: 18px 0;
    background: ${iotColor.rowHover}; border: 1px solid ${iotColor.cardLine}; border-radius: ${iotRadius.control};
    strong { font-size: 15px; } span { color: ${iotColor.textMuted}; font-size: 13px; }
    small { color: ${iotColor.textMuted}; font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }
`;
const ExportOptions = styled.fieldset`
    border: 0; margin: 0; padding: 0; min-width: 0; display: grid; gap: 12px;
    legend { font-size: 13px; color: ${iotColor.textMuted}; margin-bottom: 8px; }
`;
const ExportOption = styled.label`
    display: flex; align-items: center; gap: 12px; padding: 16px; cursor: pointer;
    border: 1px solid ${({ $selected }) => $selected ? iotColor.text : iotColor.cardLine};
    border-radius: ${iotRadius.control}; background: ${iotColor.rowHover};
    input { flex-shrink: 0; width: 17px; height: 17px; }
    span { display: flex; flex-direction: column; gap: 5px; }
    strong { font-size: 15px; } small { color: ${iotColor.textMuted}; font-size: 12px; line-height: 1.5; }
    &:focus-within { outline: 2px solid ${iotColor.textMuted}; outline-offset: 2px; }
`;
const ExportActions = styled.div`
    display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px;
`;

export default IotDeviceDetail;

const PageShell = styled.main`
    min-height: calc(100vh - 130px);
    padding: ${iotLayout.pagePadding};
    background: ${iotColor.page};
    color: ${iotColor.text};

    @media (max-width: 720px) {
        padding: ${iotLayout.pagePaddingSmall};
    }
`;

const PageInner = styled.div`
    max-width: ${iotLayout.maxWidth};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: ${iotLayout.gap};
`;

const Breadcrumb = styled.nav`
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    color: ${iotColor.textMuted};
    font-size: 12px;
    font-weight: 600;

    button {
        padding: 0;
        border: 0;
        background: none;
        color: ${iotColor.textMuted};
        font: inherit;
        cursor: pointer;
        ${iotMixin.focusRing}

        &:hover {
            color: ${iotColor.accent};
        }
    }

    span:last-child {
        overflow: hidden;
        color: ${iotColor.text};
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const HeroCard = styled.section`
    ${iotMixin.card};
    display: grid;
    grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1.05fr) auto;
    gap: 22px;
    align-items: center;
    padding: 20px 22px;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
        gap: 16px;
    }
`;

const HeroDivider = styled.div`
    width: 1px;
    height: 56px;
    background: ${iotColor.cardHeaderLine};

    @media (max-width: 1100px) {
        display: none;
    }
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;
`;

const PageTitle = styled.h1`
    margin: 0;
    color: ${iotColor.ink};
    font-size: 22px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
`;

const StatusChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border: 1px solid ${({ $tone }) => $tone.border};
    border-radius: ${iotRadius.pill};
    background: ${({ $tone }) => $tone.bg};
    color: ${({ $tone }) => $tone.fg};
    font-size: 11.5px;
    font-weight: 700;
    white-space: nowrap;

    i {
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${({ $tone }) => $tone.dot};
        box-shadow: 0 0 0 3px ${({ $tone }) => $tone.ring};
    }
`;

const SubLine = styled.p`
    margin: 7px 0 0;
    color: ${iotColor.textMuted};
    font-size: 12.5px;
    line-height: 1.5;
    overflow-wrap: anywhere;

    b {
        ${iotFont.mono};
        color: ${iotColor.textSub};
        font-weight: 600;
    }
`;

const FactGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;

    @media (max-width: 560px) {
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
    }
`;

const Fact = styled.div`min-width: 0;`;

const FactLabel = styled.div`
    margin-bottom: 5px;
    color: ${iotColor.textMuted};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
`;

const FactValue = styled.div`
    overflow: hidden;
    color: ${iotColor.ink};
    font-size: 13px;
    font-weight: 700;
    line-height: 1.35;
    font-variant-numeric: tabular-nums;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FactSub = styled.div`
    overflow: hidden;
    color: ${iotColor.textMuted};
    font-size: 11.5px;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const HeroActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;

    @media (max-width: 1100px) {
        justify-content: flex-start;
    }
`;

const buttonBase = `
    height: 38px;
    padding: 0 16px;
    border-radius: ${iotRadius.control};
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const GhostButton = styled.button`
    ${buttonBase}
    border: 1px solid ${iotColor.controlLine};
    background: ${iotColor.surface};
    color: ${iotColor.text};
    ${iotMixin.focusRing}

    &:hover:not(:disabled) {
        background: ${iotColor.rowHover};
    }
`;

const ExportTrigger = styled(GhostButton)`
    display: inline-flex;
    align-items: center;
    gap: 7px;
`;

const PrimaryButton = styled.button`
    ${buttonBase}
    border: 1px solid ${iotColor.accent};
    background: ${iotColor.accent};
    color: ${iotColor.onDark};
    ${iotMixin.focusRing}

    &:hover:not(:disabled) {
        background: ${iotColor.accentHover};
    }
`;

const DangerButton = styled.button`
    ${buttonBase}
    border: 1px solid ${iotColor.dangerSoftLine};
    background: ${iotColor.dangerSoftBg};
    color: ${iotColor.dangerSoftText};
    ${iotMixin.focusRing}
`;

const MetricSummaryGrid = styled.section`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @media (max-width: 640px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const MetricValueCard = styled.article`
    ${iotMixin.card};
    display: flex;
    flex-direction: column;
    gap: 9px;
    min-width: 0;
    padding: 14px 15px 10px;
`;

const MetricCardTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
`;

const MetricLabel = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    color: ${iotColor.textMuted};
    font-size: 11.5px;
    font-weight: 700;
    white-space: nowrap;
`;

const MetricDot = styled.span`
    display: block;
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
`;

const MetricDelta = styled.span`
    color: ${({ $direction }) => ($direction < 0 ? iotColor.deltaDown : $direction > 0 ? iotColor.deltaUp : iotColor.textMuted)};
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

const MetricReading = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
    min-width: 0;

    strong {
        color: ${iotColor.ink};
        font-size: 24px;
        font-weight: 800;
        line-height: 1;
        letter-spacing: -0.03em;
        font-variant-numeric: tabular-nums;
    }

    span {
        color: ${iotColor.textMuted};
        font-size: 10.5px;
        font-weight: 600;
        white-space: nowrap;
    }
`;

const Sparkline = styled.svg`
    display: block;
    width: 100%;
    height: 28px;
    overflow: visible;
`;

const Controls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    h2 {
        margin: 0;
        color: ${iotColor.ink};
        font-size: 16px;
        font-weight: 700;
    }
`;

const RangeTabs = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
    max-width: 100%;
    min-height: 36px;
    overflow: hidden;
    border: 1px solid ${iotColor.controlLine};
    border-radius: ${iotRadius.control};
    background: ${iotColor.surface};
`;

const RangeTab = styled.button`
    flex-shrink: 0;
    height: 34px;
    padding: 0 16px;
    border: 0;
    background: ${({ $active }) => ($active ? iotColor.accent : iotColor.surface)};
    color: ${({ $active }) => ($active ? iotColor.onDark : iotColor.textMuted)};
    font-family: inherit;
    font-size: 12.5px;
    font-weight: ${({ $active }) => ($active ? 700 : 600)};
    cursor: pointer;
    ${iotMixin.focusRing}

    & + & {
        border-left: 1px solid ${iotColor.cardLine};
    }
`;

const ResolutionNote = styled.span`
    color: ${iotColor.textMuted};
    font-size: 12px;
    font-weight: 600;
`;

const GasChartGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${iotLayout.gap};

    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ChartPanel = styled.article`
    ${iotMixin.card};
    min-width: 0;
    padding: ${({ $compact }) => ($compact ? '18px 20px 14px' : '18px 20px 16px')};
`;

const ChartPanelHeader = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 12px;

    h3 {
        margin: 0;
        color: ${iotColor.ink};
        font-size: 15px;
        font-weight: 700;
    }

    p {
        margin: 4px 0 0;
        color: ${iotColor.textMuted};
        font-size: 12px;
    }
`;

const LegendRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
`;

const LegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${iotColor.text};
    font-size: 12px;
    font-weight: 700;

    b {
        color: ${iotColor.textMuted};
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }
`;

const LegendSwatch = styled.span`
    display: block;
    width: 14px;
    height: 2.5px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
`;

const UnitPill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border: 1px solid ${iotColor.cardLine};
    border-radius: ${iotRadius.pill};
    background: ${iotColor.headerRow};
    color: ${iotColor.textMuted};
    font-size: 11.5px;
    font-weight: 600;
`;

const CurrentValue = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;

    strong {
        color: ${iotColor.ink};
        font-size: 19px;
        font-weight: 800;
        letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
    }

    span {
        color: ${iotColor.textMuted};
        font-size: 11px;
        font-weight: 600;
    }
`;

const ChartViewport = styled.div`
    height: ${({ $compact }) => ($compact ? '180px' : '260px')};
`;

const ChartEmpty = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    border-radius: ${iotRadius.control};
    background: ${iotColor.headerRow};
    color: ${iotColor.textMuted};
    font-size: 12.5px;
`;

const Message = styled.div`
    padding: 12px 16px;
    border: 1px solid ${({ $error }) => ($error ? CONNECTION_TONES.오프라인.border : CONNECTION_TONES.온라인.border)};
    border-radius: ${iotRadius.control};
    background: ${({ $error }) => ($error ? CONNECTION_TONES.오프라인.bg : CONNECTION_TONES.온라인.bg)};
    color: ${({ $error }) => ($error ? CONNECTION_TONES.오프라인.fg : CONNECTION_TONES.온라인.fg)};
    font-size: 13px;
`;

const StatePanel = styled.div`
    ${iotMixin.card};
    max-width: ${iotLayout.maxWidth};
    margin: 28px auto;
    padding: 24px;
    color: ${iotColor.textMuted};
`;
